<script lang="ts" setup>
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppImagesLoader, AppLazyLoadComponent } from '@libs/ui';

import type {
  TripSummarySegment,
  TripSummarySegmentPrice,
} from '../../../models';
import {
  ItinerarySegmentType,
  TripSegmentDetailType,
  isTransportSegment,
  isFlightSegment,
  isHotelSegment,
  isActivitySegment,
} from '../../../models';

import TripSummaryCardLayout from './TripSummaryCardLayout.vue';

interface TripSummaryCardDesktopProps {
  segment: TripSummarySegment;
  hidePrices?: boolean;
}

interface HeaderElement {
  text: string;
  type: TripSegmentDetailType;
}

const props = defineProps<TripSummaryCardDesktopProps>();
const { t } = i18n.global;

const imageType = computed((): 'contain' | 'cover' =>
  isTransportSegment(props.segment) ? 'contain' : 'cover'
);

const imageAlt = computed((): string => {
  if (props.segment.summaryType === ItinerarySegmentType.Flight) {
    return t('a11y.flight_carrier_logo');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Ferry) {
    return t('a11y.ferry_carrier_logo');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Accommodation) {
    return t('a11y.hotel_image');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Activity) {
    return t('a11y.activity_image');
  }
  return t('a11y.image');
});

const headerElements = computed((): HeaderElement[] => {
  const elements: HeaderElement[] = [
    { text: props.segment.title, type: TripSegmentDetailType.Title },
    { text: props.segment.date, type: TripSegmentDetailType.Date },
  ];

  if (isFlightSegment(props.segment) && props.segment.stops) {
    elements.push({
      text: props.segment.stops,
      type: TripSegmentDetailType.Stops,
    });
  }

  if (isActivitySegment(props.segment)) {
    elements.push({
      text: `${props.segment.numberOfTickets} tickets`,
      type: TripSegmentDetailType.Tickets,
    });
  }

  if (isHotelSegment(props.segment)) {
    elements.push({
      text: `${props.segment.nights} nights`,
      type: TripSegmentDetailType.Nights,
    });
  }

  return elements;
});

const priceTitle = computed(() => (price: TripSummarySegmentPrice): string => {
  if (isTransportSegment(props.segment)) {
    return props.segment.time;
  }
  return price.description;
});

const priceLabel = computed(() => (price: TripSummarySegmentPrice): string => {
  if (isTransportSegment(props.segment)) {
    return price.description;
  }
  if (isHotelSegment(props.segment) || isActivitySegment(props.segment)) {
    return props.segment.location || '';
  }
  return '';
});

function getTicketsText(numberOfTickets: number): string {
  return `${numberOfTickets} ${t(
    'planner.tickets',
    numberOfTickets
  ).toLowerCase()}`;
}

function getNightsText(nights: number): string {
  return `${nights} ${t('planner.nights', nights).toLowerCase()}`;
}
</script>

<template>
  <TripSummaryCardLayout>
    <template #image>
      <AppLazyLoadComponent>
        <AppImagesLoader
          v-if="props.segment.images"
          :key="props.segment.images.join('-')"
          :urls="props.segment.images"
          :alt="imageAlt"
          :img-type="imageType"
        />
      </AppLazyLoadComponent>
    </template>

    <template #header>
      <span
        v-for="(element, index) in headerElements"
        :key="index"
        class="segment-element"
      >
        <template
          v-if="
            element.type === TripSegmentDetailType.Tickets &&
            isActivitySegment(props.segment)
          "
        >
          {{ getTicketsText(props.segment.numberOfTickets) }}
        </template>
        <template
          v-else-if="
            element.type === TripSegmentDetailType.Nights &&
            isHotelSegment(props.segment)
          "
        >
          {{ getNightsText(props.segment.nights) }}
        </template>
        <template v-else>
          {{ element.text }}
        </template>
      </span>
    </template>

    <template #content>
      <div
        v-for="(price, priceIndex) of props.segment.price"
        :key="`${priceIndex}-${price.description}-${price.value}`"
        class="segment-price"
      >
        <div class="segment-price-title">
          <span class="segment-title">
            {{ priceTitle(price) }}
          </span>
        </div>

        <div class="segment-price-value-container">
          <div class="segment-price-label">
            {{ priceLabel(price) }}
          </div>
          <div v-if="!props.hidePrices" class="segment-price-value">
            {{ price.value }}
          </div>
        </div>
      </div>
    </template>
  </TripSummaryCardLayout>
</template>

<style scoped lang="scss">
.segment-element {
  @include font-label-s-light;
  white-space: nowrap;
  padding: 0 var(--spacing-8);

  &:not(:last-child) {
    border-right: 1px solid var(--color-neutral-20);
  }

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
}

.segment-price {
  display: flex;
  flex-direction: column;

  .segment-price-title {
    margin-bottom: var(--spacing-4);

    .segment-title {
      @include font-label-m-bold;
      display: flex;
      gap: var(--spacing-12);
    }
  }

  .segment-price-value-container {
    display: flex;
    justify-content: space-between;

    .segment-price-value {
      @include font-label-m-light;
    }

    .segment-price-label {
      @include font-label-s-light;
    }
  }
}
</style>
